import { useState, useMemo, useEffect, useCallback, useReducer } from 'react';
import { useNavigate, useParams } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/react';
import { v4 as uuidv4 } from 'uuid';
import * as heap from 'helpers/heap';
import * as mparticle from 'helpers/mparticle';
import { resolveUserRole, trackUserLoggedIn, trackUserLoggedOut } from 'helpers/dashboardAnalytics';

import { apiRequest, apiRequestErrorToError } from 'helpers/api';
import * as authHelper from 'helpers/auth.cuboh';

import { COMPANY_UPDATED } from 'constants/pusher';
import {
  setError,
  setData,
  setFeatureFlags,
  updateCompany,
  updateMerchant,
  updateParentCompany,
  setProxyUserID,
  setUserMeta,
  setCorpaImpersonation,
} from 'actions/auth';

import { reducer, DEFAULT_STATE, initializeCorpaImpersonation } from 'reducers/auth';
import { IS_CORPA_IMPERSONATION } from 'constants/cuboh';

import useCubohAuthToken from 'hooks/useCubohAuthToken';
import { useInjectTalkDesk } from 'hooks/useInjectTalkDesk';
import { usePusher } from 'hooks/usePusher';
import {
  fetchMe,
  consolidatedLogin,
  consolidatedPasswordReset,
  CONSOLIDATED_RESET_CUBOH_RESET_PASSWORD,
} from 'helpers/auth';

export default function useProvideAuth() {
  const navigate = useNavigate();
  const [user, setUser] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasLogoutError, setHasLogoutError] = useState(false);
  const { setChannelNames, pusher, channels } = usePusher();
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE, initializeCorpaImpersonation);
  const { cubohUser, companies, merchants } = state;
  const { restaurantId, hqId } = useParams();

  // This happens for any account that has Cuboh, aka does not get called for CN only accounts
  const getUserData = useCallback(async ({ chowNowUser = null } = {}) => {
    try {
      dispatch(setError({ hasError: false, message: '' }));
      const [newUserData, featureFlags] = await Promise.all([
        await authHelper.fetchUserData(),
        await authHelper.fetchFeatureFlags(),
      ]);
      dispatch(setData({ data: newUserData }));
      dispatch(setFeatureFlags({ featureFlags }));
      if (chowNowUser) {
        setUser(chowNowUser);
      } else {
        setUser(newUserData.chownowUser);
      }
    } catch (e) {
      dispatch(setError({ hasError: true, message: e.message }));
    }

    setIsLoading(false);
  }, []);

  const { cnAccessToken, setTokens, clearTokens } = useCubohAuthToken({
    refreshOnPageLoad: true,
    keepTokenAlive: true,
  });

  const validCubohOnlyTokenUser = async accessToken => {
    if (!accessToken) return false;
    const tokenIsIssuedByChowNow = authHelper.tokenIsIssuedByChowNow(accessToken);
    const validToken = await authHelper.verifyToken();
    if (!tokenIsIssuedByChowNow && validToken) return true;
    return false;
  };

  const userHasCubohUserID = useMemo(
    () => authHelper.tokenHasCubohUserId(authHelper.getAccessToken()),
    [user, cubohUser],
  );

  const isUserCubohOnly = !!(!user?.id && cubohUser);
  const isUserChowNowOnly = !!(user?.id && !cubohUser);
  const isUserCubohAndChowNow = !!(user?.id && cubohUser);
  const isInternalUser = user?.is_chownow_admin || user?.is_super_admin;
  const hasChowNowUser = isUserChowNowOnly || isUserCubohAndChowNow;
  const hasCubohUser = isUserCubohOnly || isUserCubohAndChowNow;
  const isImpersonating = !!cubohUser?.tokenClaims?.proxy_user;

  // This is for CN internal users to load Cuboh data
  useEffect(() => {
    if (
      !user ||
      !isInternalUser ||
      (!restaurantId && !hqId) ||
      isLoading ||
      (isInternalUser && restaurantId.startsWith('cuboh-'))
    ) {
      return;
    }

    const checkAndFetchCubohData = async () => {
      let cubohCompanyData;
      try {
        if (restaurantId && !hqId) {
          cubohCompanyData = await authHelper.fetchUserDataByCnRestaurantID(restaurantId);
        } else {
          cubohCompanyData = await authHelper.fetchCubohUserDataByCnHq(hqId);
        }
        if (cubohCompanyData) {
          dispatch(setData({ data: cubohCompanyData }));
        }
      } catch (error) {
        /* empty because functions above log the error before throwing */
      }
    };
    checkAndFetchCubohData();
  }, [restaurantId, user, hqId, isLoading]);

  // Inject talkdesk script right before body close if user logged in & is not a Cuboh-only user
  useInjectTalkDesk({
    isUserCubohOnly,
    isAuthenticated: user?.id,
  });

  useEffect(() => {
    const checkUserLoggedIn = async () => {
      try {
        const accessToken = authHelper.getAccessToken();

        if (await validCubohOnlyTokenUser(accessToken)) {
          await getUserData();
          return;
        }

        const { data } = await fetchMe();

        if (!data) return;

        setUser({
          ...data.data,
          metadata: data.metadata,
        });
      } catch (error) {
        /* empty */
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };
    checkUserLoggedIn();
  }, []);

  useEffect(() => {
    if (!cnAccessToken || !user || !authHelper.tokenHasCubohUserId(cnAccessToken)) {
      return;
    }

    void getUserData({ chowNowUser: user });
  }, [cnAccessToken, user?.id]);

  useEffect(() => {
    const userId = user?.id ?? cubohUser?.id;
    const isChowNowUser = !!user?.id;

    if (userId) {
      const ddUserId = `${isChowNowUser ? 'cn' : 'cuboh'}:${userId}`;

      Sentry.setUser({
        id: userId,
        is_chownow_user: !!isChowNowUser,
        is_chownow_admin: isInternalUser,
      });
      datadogRum.setUser({ id: ddUserId });

      // Identify in mParticle for all entry points (fresh tab / session
      // restore / cuboh-merged login), not just the explicit `login()` path.
      // `identifyUser` is deduped by user id internally so repeat calls are
      // safe. ChowNow user is preferred; we currently only have ChowNow user
      // shape required by `identifyUser`.
      if (user?.id) {
        mparticle.identifyUser(user);
      }
    } else {
      Sentry.setUser(null);
      datadogRum.clearUser();
      mparticle.logoutUser();
    }
  }, [cubohUser, isInternalUser, user]);

  // Identify user on analytics applications
  useEffect(() => {
    if (!cubohUser?.id) return;

    setChannelNames(
      Object.values(companies)?.map(
        company => `${import.meta.env.VITE_WEB_SOCKET_STAGE}-company-${company.id}`,
      ),
    );

    if (cubohUser.id) {
      if (cubohUser.tokenClaims?.proxy_user) {
        dispatch(setProxyUserID({ proxyUserID: cubohUser.tokenClaims?.proxy_user }));
      }
    }
  }, [cubohUser]);

  useEffect(() => {
    if (!channels?.length > 0) {
      return;
    }

    const setupPusherEvents = async () => {
      channels.forEach(channel => {
        channel.bind(COMPANY_UPDATED, async company => {
          dispatch(
            updateCompany({
              id: company.id,
              payload: company,
            }),
          );
        });
      });
    };

    setupPusherEvents();
  }, [channels]);

  /**
   * Check whether the user has permission to perform an action
   * @param permission: String permission constant from auth
   *
   * @returns if user has permission, default to false for no user in state
   */
  const hasPermission = useCallback(
    permission => {
      if (!cubohUser) {
        return false;
      }

      return cubohUser.permissions.includes(permission);
    },
    [cubohUser],
  );

  const updateCompanyDetails = ({ company }) => {
    try {
      dispatch(setError({ hasError: false, message: '' }));
      dispatch(updateCompany({ id: company.id, payload: company }));
    } catch (e) {
      dispatch(setError({ hasError: true, message: e.message }));
    }
  };

  const updateMerchantDetails = ({ merchant }) => {
    try {
      dispatch(setError({ hasError: false, message: '' }));
      dispatch(updateMerchant({ id: merchant.id, payload: merchant }));
    } catch (e) {
      dispatch(setError({ hasError: true, message: e.message }));
    }
  };

  const updateCompanyMeta = ({ companyID, company_meta: companyMeta }) => {
    dispatch(
      updateCompany({
        id: companyID,
        payload: { company_meta: companyMeta },
      }),
    );
  };

  const updateMerchantMeta = ({ merchantID, merchant_meta: merchantMeta }) => {
    dispatch(
      updateMerchant({
        id: merchantID,
        payload: { merchant_meta: merchantMeta },
      }),
    );
  };

  const bulkUpdateMerchantDetails = ({ updatedMerchants }) => {
    try {
      dispatch(setError({ hasError: false, message: '' }));
      const merchantsCopy = { ...state.merchants };

      updatedMerchants.forEach(updatedMerchant => {
        merchantsCopy[updatedMerchant.id] = {
          ...merchantsCopy[updatedMerchant.id],
          ...updatedMerchant,
        };
      });

      dispatch(
        setData({
          data: { merchants: merchantsCopy },
        }),
      );
    } catch (e) {
      dispatch(setError({ hasError: true, message: e.message }));
    }
  };

  const updateParentCompanyDetails = ({ parentCompany }) => {
    try {
      dispatch(setError({ hasError: false, message: '' }));
      dispatch(updateParentCompany({ parentCompany }));
    } catch (e) {
      dispatch(setError({ hasError: true, message: e.message }));
    }
  };

  const updateUserMetaForUser = async ({ userMeta, failureMessage }) => {
    try {
      dispatch(setError({ hasError: false, message: '' }));
      const newUserMeta = await authHelper.updateUserMeta({
        userMeta,
        failureMessage,
      });
      dispatch(
        setUserMeta({
          userMeta: newUserMeta,
        }),
      );
    } catch (e) {
      dispatch(setError({ hasError: true, message: e.message }));
    }
  };

  const flags = useMemo(() => cubohUser?.flags || {}, [cubohUser]);

  const notificationID = useMemo(() => (cubohUser ? uuidv4() : undefined), [cubohUser]);

  const getMerchantIDsForCompany = useCallback(
    companyID =>
      Object.keys(merchants ?? {}).filter(
        merchantID => Number(merchants[merchantID].companyID) === Number(companyID),
      ),
    [merchants],
  );
  const cubohLogout = () => {
    clearTokens();
    window.localStorage.removeItem(IS_CORPA_IMPERSONATION);
    dispatch(setData({ data: DEFAULT_STATE }));
    pusher?.disconnect();
  };

  async function logout() {
    const { error } = await apiRequest({
      path: 'admin/logout',
      method: 'POST',
    });
    cubohLogout();
    Sentry.setUser(null);

    if (error) {
      setHasLogoutError(true);
      return;
    }

    // Fire before clearing `user` so we can still resolve the role.
    trackUserLoggedOut({ userRole: resolveUserRole(user) });
    heap.resetIdentity();
    setUser(null);
    navigate('/logout', { replace: true });
  }

  /**
   * Log in to dashboard
   * @param  {string} email - User account email
   * @param  {string} password - User password
   * @param  {string} uniqueKey - verification code sent to user's email to reset password
   * @param  {boolean} [setPasswordViaLogin] - set the password through admin-api login, which accepts the code directly
   */
  async function login(email, password, uniqueKey, setPasswordViaLogin = false) {
    let result;
    if (uniqueKey && setPasswordViaLogin) {
      result = await consolidatedLogin(email, password, uniqueKey);
    } else if (uniqueKey) {
      result = await consolidatedPasswordReset(email, password, uniqueKey);
    } else {
      result = await consolidatedLogin(email, password);
    }
    const { data, error } = result;

    if (error) {
      throw apiRequestErrorToError(error);
    } else if (typeof data === 'string') {
      if (data === CONSOLIDATED_RESET_CUBOH_RESET_PASSWORD) {
        navigate('/login');
        return;
      }

      throw new Error(`[useProvideAuth:login] unhandled string state: ${data}`);
    }

    const { chownow, cuboh: cubohSession } = data;

    if (chownow) {
      const chowNowUserResponse = await fetchMe();

      if (chowNowUserResponse.error) {
        throw apiRequestErrorToError(chowNowUserResponse.error);
      }

      const chowNowUser = {
        ...chowNowUserResponse.data.data,
        metadata: chowNowUserResponse.data.metadata,
      };

      if (!cubohSession || !(await authHelper.verifyToken())) {
        setUser(chowNowUser);
        heap.initUser(chowNowUser);
        trackUserLoggedIn({
          loginMethod: 'email_password',
          userRole: resolveUserRole(chowNowUser),
        });
        return;
      }

      await getUserData({ chowNowUser });
      trackUserLoggedIn({
        loginMethod: 'email_password',
        userRole: resolveUserRole(chowNowUser),
      });
      return;
    }

    if (cubohSession) {
      setTokens(cubohSession.access_token, cubohSession.refresh_token);
      await getUserData();
      // Cuboh-only sessions have no ChowNow admin flags, so role is always 'operator'.
      trackUserLoggedIn({ loginMethod: 'email_password', userRole: 'operator' });
      return;
    }

    throw new Error('invalid consolidated auth state');
  }

  // No `user_logged_out` event — this exits impersonation; the admin is not logging out.
  async function clearUsersForImpersonation() {
    const { error } = await apiRequest({
      path: 'admin/logout',
      method: 'POST',
    });
    cubohLogout();
    Sentry.setUser(null);
    if (!error) {
      heap.resetIdentity();
      setUser(null);
    }
  }

  return {
    login,
    logout,
    hasLogoutError,
    setHasLogoutError,
    setUser,
    user,
    isLoading,
    setIsLoading,
    getUserData,
    updateCompanyDetails,
    updateMerchantDetails,
    bulkUpdateMerchantDetails,
    updateParentCompanyDetails,
    hasPermission,
    flags,
    featureFlags: state?.featureFlags,
    userMeta: state?.userDetails?.userMeta,
    isGuest: false,
    notificationID,
    updateUserMetaForUser,
    updateCompanyMeta,
    updateMerchantMeta,
    getMerchantIDsForCompany,
    isInitialLoad,
    setIsInitialLoad,
    userHasCubohUserID,
    isCompanyAdmin: user?.is_company_admin,
    isUserChowNowOnly,
    isUserCubohOnly,
    isUserCubohAndChowNow,
    isInternalUser,
    isRegionalAdminUser: user?.is_region_admin,
    hasChowNowUser,
    hasCubohUser,
    isImpersonating,
    isCorpaImpersonation: state?.isCorpaImpersonation,
    setIsCorpaImpersonation: () => {
      window.localStorage.setItem(IS_CORPA_IMPERSONATION, 'true');
      dispatch(setCorpaImpersonation({ isCorpaImpersonation: true }));
    },
    clearUsersForImpersonation,
    ...state,
  };
}
